import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Steuersatz, SteuersatzAnlegen } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, useBeforeUnload,
         useFormularZustand } from "@kundeninfo/ui";
import { Formular } from "@kundeninfo/ui/me-design";
import { formatDatum } from "../../format.js";
import { KEINE_ZAHL, Meldung, SATZ_FORMAT, alsZahl, eingabeUnlesbar,
         meldungAus } from "./gemeinsam.js";
import "../Verwaltung.css";
import { KiKnopf } from "../../components/meHuellen.js";

/**
 * Der Verwaltungsbereich STEUERSÄTZE (taxRate, Paket 3b-1a).
 *
 * Herausgelöst aus views/Verwaltung.tsx (Etappe 2, Aufgabe 3) — rein strukturell, CSS und
 * Verhalten unverändert (Block `vw__`, views/Verwaltung.css).
 *
 * **Ohne zweite Ebene** (Entscheid Aufgabe 3): Das Objekt dieses Bereichs ist nicht die
 * Zeile, sondern die ZEITREIHE. Eine einzelne Zeitscheibe ist nur neben ihren Nachbarn zu
 * verstehen — die Marke „gilt heute" entsteht erst aus dem Vergleich innerhalb der Serie,
 * und „Fortschreiben" ist eine Handlung an der Serie, nicht an einer Scheibe. Eine Serie
 * wiederum hat keine eigene Kennung und damit keine Adresse `/verwaltung/steuersaetze/<id>`,
 * die man ihr geben könnte; sie über den Namen zu adressieren, hiesse eine zweite Identität
 * neben der Kennung einzuführen. Der Bereich steht deshalb rechts als Ganzes.
 *
 * Ein Steuersatz ist keine Zeile, sondern eine Serie von Zeitscheiben (gleicher Name): 19 %
 * ab 2007, 16 % ab Juli 2020, 19 % ab 2021. Eine Änderung des Satzes ist deshalb im
 * Regelfall KEINE Änderung der bestehenden Zeile, sondern eine NEUE Zeitscheibe — dafür
 * steht an jeder Serie „Fortschreiben" (der Name ist vorbelegt, zu füllen sind Satz und
 * Gültig-ab). „Ändern" bleibt für den Fall, dass jemand sich vertippt hat.
 *
 * Wie bei den Datei-Kategorien gibt es keinen Versionsvergleich (last-write-wins) und kein
 * Archivieren, sondern Stilllegen über einen eigenen Klick.
 */

/** Nur die vier Wege dieses Bereichs — so lässt er sich im Test mit vier vi.fn zeichnen. */
export type SteuersaetzeClient = Pick<ApiClient,
  "listTaxRates" | "createTaxRate" | "updateTaxRate" | "setTaxRateActive">;

type SteuersatzDialog = {
  /** null = neue Zeitscheibe. */
  id: string | null;
  name: string;
  ratePercent: string;
  validFrom: string;
};

type SteuersatzFeld = "name" | "ratePercent" | "validFrom";

const STEUERSATZ_FELDER: readonly SteuersatzFeld[] = ["name", "ratePercent", "validFrom"];

function steuersatzdialogAus(s: Steuersatz | null): SteuersatzDialog {
  if (s === null) return { id: null, name: "", ratePercent: "", validFrom: "" };
  return { id: s.id, name: s.name, ratePercent: String(s.ratePercent),
           validFrom: s.validFrom };
}

function steuersatzdialogGleich(a: SteuersatzDialog, b: SteuersatzDialog): boolean {
  return a.id === b.id && a.name === b.name && a.ratePercent === b.ratePercent
    && a.validFrom === b.validFrom;
}

/** Heute als ISO-Kalendertag in ORTSZEIT — `toISOString()` wäre hier falsch: Es rechnet
 *  nach UTC um und läge östlich von Greenwich am Abend schon auf dem Folgetag. */
function heuteIso(): string {
  const jetzt = new Date();
  return `${jetzt.getFullYear()}-${String(jetzt.getMonth() + 1).padStart(2, "0")}`
    + `-${String(jetzt.getDate()).padStart(2, "0")}`;
}

/**
 * Eine Serie: alle Zeitscheiben gleichen Namens, die jüngste zuerst — genau die Reihenfolge,
 * in der der Server sie liefert; hier wird nur gruppiert, nicht umsortiert.
 */
type Serie = { name: string; scheiben: Steuersatz[] };

function serien(zeilen: Steuersatz[]): Serie[] {
  const nach = new Map<string, Serie>();
  for (const s of zeilen) {
    // Gross-/Kleinschreibung trennt keine Serie — die Spalte steht auf ai_ci, der Server
    // sieht es genauso. Der zuerst gelieferte Name benennt die Serie.
    const schluessel = s.name.toLowerCase();
    const bisher = nach.get(schluessel);
    if (bisher === undefined) nach.set(schluessel, { name: s.name, scheiben: [s] });
    else bisher.scheiben.push(s);
  }
  return [...nach.values()];
}

/**
 * Die zum Datum geltende Zeitscheibe einer Serie: die jüngste mit `validFrom <= datum`.
 *
 * Dieselbe Regel wie `steuersatzZumDatum` in `domain` — die die Oberfläche NICHT importieren
 * darf (web kennt nur `ui` und `api-client`, siehe scripts/check-boundaries.mjs). Das ist
 * hier vertretbar, weil die Marke „gilt heute" reine Anzeige ist: Verbindlich rechnet der
 * Server. Die Doppelung steht damit benannt da, wie bei `formatKondition` in format.ts.
 */
function geltendeScheibe(serie: Serie, datum: string): Steuersatz | null {
  let gewinner: Steuersatz | null = null;
  for (const s of serie.scheiben) {
    if (s.validFrom <= datum && (gewinner === null || s.validFrom > gewinner.validFrom)) {
      gewinner = s;
    }
  }
  return gewinner;
}

export function SteuersaetzeBereich(
  { client }: { client: SteuersaetzeClient },
): ReactElement {
  const [zeilen, setZeilen] = useState<Steuersatz[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<SteuersatzDialog | null>(null);
  /** Fehlschlag des Stilllegens/Einschaltens an der Liste (kein Formular). */
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Die Zeitscheibe, deren Aktiv-Zustand gerade umgeschaltet wird (Knopf sperren). */
  const [schaltet, setSchaltet] = useState<string | null>(null);

  const listTaxRates = client.listTaxRates;

  useEffect(() => {
    let abgebrochen = false;
    listTaxRates()
      .then((antwort) => { if (!abgebrochen) setZeilen(antwort.items); })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(meldungAus(fehler, "Die Steuersätze konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [listTaxRates]);

  /**
   * Formularzustände der Maske (ME-Etappe 3, Aufgabe 6). `taxRate` trägt keine Version
   * (last-write-wins) — eine 409 gibt es nicht. Die Zeitscheiben-Mechanik bleibt beim
   * Server: Anlegen ist eine NEUE Scheibe (der Nachfolger beendet den Vorgänger über sein
   * Gültig-ab), und nach jedem Schreiben wird neu gelesen, weil die Ordnung der Reihe von
   * dort kommt.
   */
  const formular = useFormularZustand<true>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Keine Zeitscheibe offen.");
      const eingabe = eingabeAus(d);
      if (d.id === null) {
        // Anlegen antwortet nur mit der Kennung — die Liste neu lesen, damit die neue
        // Zeitscheibe an ihrem Platz in der Serie steht.
        await client.createTaxRate(eingabe);
      } else {
        // Auch beim Ändern neu lesen: Ein geändertes `validFrom` (oder gar ein anderer
        // Serienname) verschiebt die Zeile innerhalb der Zeitreihe, und die Ordnung kommt
        // vom Server. Der zurückgegebene Stand geht dabei nicht verloren — die Liste ist
        // die frischere Wahrheit.
        await client.updateTaxRate(d.id, eingabe);
      }
      const antwort = await client.listTaxRates();
      setZeilen(antwort.items);
      return true;
    },
    neuLaden: async () => {
      const antwort = await client.listTaxRates();
      setZeilen(antwort.items);
    },
    bekannteFelder: STEUERSATZ_FELDER,
    beschriftungen: { name: "Name", ratePercent: "Steuersatz in Prozent",
                      validFrom: "Gültig ab" },
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<SteuersatzFeld, string>>;

  const dialogGeaendert = dialog !== null && !steuersatzdialogGleich(
    dialog, steuersatzdialogAus(zeilen?.find((z) => z.id === dialog.id) ?? null));
  useBeforeUnload(dialogGeaendert);

  /** Der eine Weg aus dem Modal neben dem Speichern (Hausregel „striktes Modalpanel",
   *  06.09.2026) — mit Rückfrage, wenn etwas Ungespeichertes darin steht. Escape ruft
   *  denselben Weg (siehe `Modal` in @kundeninfo/ui); ein Klick daneben schliesst nicht. */
  async function abbrechen(): Promise<void> {
    // Kein Ausgang, solange geschrieben wird — das gilt für die ESCAPE-Taste genauso wie
    // für den (dann gesperrten) Knopf: Beide gehen durch diese Funktion, und die Antwort
    // des Servers braucht ihr Panel noch (Fixrunde 1).
    if (arbeitet) return;
    if (!(await darfAbbrechen(dialogGeaendert))) return;
    setDialog(null);
  }

  function oeffnen(s: Steuersatz | null): void {
    formular.zuruecksetzen();
    setMeldung(null);
    setDialog(steuersatzdialogAus(s));
  }

  /** Fortschreiben: neue Zeitscheibe MIT dem Seriennamen — der bleibt, alles andere ist neu. */
  function fortschreiben(name: string): void {
    formular.zuruecksetzen();
    setMeldung(null);
    setDialog({ id: null, name, ratePercent: "", validFrom: "" });
  }

  /** Der Rumpf — wirft die eigene 422, wenn der Satz keine Zahl ist. Der Satz bleibt ein
   *  TEXTfeld mit Dezimalkomma („16,5"), kein Zahlfeld: Ein `type=number` des Browsers
   *  verwürfe das Komma je nach Spracheinstellung still. */
  function eingabeAus(d: SteuersatzDialog): SteuersatzAnlegen {
    const ratePercent = alsZahl(d.ratePercent);
    if (ratePercent === KEINE_ZAHL || ratePercent === null) {
      throw eingabeUnlesbar([{ field: "ratePercent",
                               message: "Bitte einen Prozentwert eintragen." }]);
    }
    // Die Fachprüfung (0..100, zwei Nachkommastellen, brauchbares Datum) macht der Server —
    // hier steht nur die Umwandlung, die scheitern kann.
    return { name: d.name, ratePercent, validFrom: d.validFrom };
  }

  async function speichern(): Promise<void> {
    if (await formular.aktion() === true) setDialog(null);
  }

  async function umschalten(s: Steuersatz): Promise<void> {
    setMeldung(null);
    setSchaltet(s.id);
    try {
      const neu = await client.setTaxRateActive(s.id, !s.active);
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
    } catch (fehler) {
      setMeldung(meldungAus(fehler, "Der Zustand konnte nicht geändert werden."));
    } finally {
      setSchaltet(null);
    }
  }

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;

  const heute = heuteIso();
  const gruppen = serien(zeilen);

  return (
    <>
      {meldung !== null && dialog === null && <Meldung text={meldung} />}
      <div className="vw__leiste">
        <KiKnopf art="primaer" onClick={() => oeffnen(null)}>
          Neuer Steuersatz
        </KiKnopf>
      </div>
      <p className="vw__hinweis">
        Ein Satz gilt ab seinem Gültig-ab-Datum, bis die nächste Zeitscheibe derselben Reihe
        beginnt. Ein Enddatum gibt es nicht — zum Ändern wird fortgeschrieben.
      </p>

      {gruppen.map((serie) => {
        const geltend = geltendeScheibe(serie, heute);
        return (
          <section key={serie.name.toLowerCase()} className="vw__serie">
            <div className="vw__serie-kopf">
              <h4 className="vw__serie-name">{serie.name}</h4>
              <KiKnopf name={`${serie.name} fortschreiben`} onClick={() => fortschreiben(serie.name)}>
                Fortschreiben
              </KiKnopf>
            </div>
            <ul className="vw__liste">
              {serie.scheiben.map((s) => {
                const ab = formatDatum(s.validFrom);
                return (
                  <li key={s.id} className="vw__eintrag">
                    <div className="vw__eintrag-kopf">
                      <span className="vw__eintrag-name">
                        {SATZ_FORMAT.format(s.ratePercent)} %
                      </span>
                      {geltend !== null && geltend.id === s.id && (
                        <span className="vw__marke">gilt heute</span>
                      )}
                      {!s.active && <span className="vw__marke">stillgelegt</span>}
                    </div>
                    <span className="vw__eintrag-zusatz">gültig ab {ab}</span>
                    <div className="vw__eintrag-aktionen">
                      <KiKnopf name={`Zeitscheibe ab ${ab} ändern`} onClick={() => oeffnen(s)}>
                        Ändern
                      </KiKnopf>
                      <KiKnopf deaktiviert={schaltet === s.id} name={s.active
                          ? `Zeitscheibe ab ${ab} stilllegen`
                          : `Zeitscheibe ab ${ab} wieder einschalten`} onClick={() => { void umschalten(s); }}>
                        {s.active ? "Stilllegen" : "Wieder einschalten"}
                      </KiKnopf>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      {dialog !== null && (
        <Modal titel={dialog.id === null ? "Neue Zeitscheibe" : "Zeitscheibe ändern"}
          onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
          {formular.meldung !== null && <Meldung text={formular.meldung} />}
          <Formular beschriftung="oben" {...formular.formularProps}>
            <Feld id="vw-s-name" label="Name" bearbeitbar wert={dialog.name}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, name: w }))}
              {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
            <Feld id="vw-s-rate" label="Steuersatz in Prozent" bearbeitbar
              wert={dialog.ratePercent}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, ratePercent: w }))}
              {...(feldFehler.ratePercent !== undefined
                ? { fehler: feldFehler.ratePercent } : {})} />
            <Feld id="vw-s-validFrom" label="Gültig ab" bearbeitbar wert={dialog.validFrom}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, validFrom: w }))}
              {...(feldFehler.validFrom !== undefined
                ? { fehler: feldFehler.validFrom } : {})} />
          </Formular>
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={arbeitet}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </>
  );
}
